import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Loader2, Plus, ClipboardList } from 'lucide-react';
import { quotationAPI } from '../../services/api';
import usePersistedForm from '../../hooks/usePersistedForm';
import SuccessScreen from '../ui/SuccessScreen';
import BeautifiedErrorAlert from '../ui/BeautifiedErrorAlert';
import { cn } from '../../lib/utils';
import QuotationItemForm from './QuotationItemForm';
import useServerValidation from '../../hooks/useServerValidation';

const CreateQuotationModal = ({ onClose, products, onSuccess, onError }) => {
    const [submitError, setSubmitError] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [countdown, setCountdown] = useState(3);
    const [errors, setErrors] = useState({});
    const { applyServerErrors } = useServerValidation({
        setErrors,
        fieldMapper: (field) =>
            field
                .replace(/items\[(\d+)\]\.(\w+)/g, 'items_$1_$2')
    });

    // Success Redirection Timer
    useEffect(() => {
        let timer;
        if (isSuccess && countdown > 0) {
            timer = setInterval(() => setCountdown(c => c - 1), 1000);
        } else if (isSuccess && countdown === 0) {
            onClose();
        }
        return () => clearInterval(timer);
    }, [isSuccess, countdown, onClose]);

    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData, resetDraft] = usePersistedForm('quotationFormDraft', {
        customerName: '',
        customerAddress: '',
        quotationDate: new Date().toISOString().split('T')[0],
        items: [
            { productId: '', quantity: 1, price: 0, purchasePrice: 0, gstPercent: 0, customDescription: '', productName: '', _isManual: false }
        ],
        notes: ''
    });



    const handleAddItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, { productId: '', quantity: 1, price: 0, purchasePrice: 0, gstPercent: 0, customDescription: '', productName: '', _isManual: false }]
        }));
    };

    const handleRemoveItem = (index) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const handleUpdateItem = (index, field, value) => {
        setFormData(prev => {
            const newItems = [...prev.items];
            newItems[index] = { ...newItems[index], [field]: value };
            return { ...prev, items: newItems };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError(null);
        setErrors({});

        // Basic validation
        const newErrors = {};
        if (!formData.customerName?.trim()) newErrors.customerName = 'Customer Name is required';
        if (!formData.quotationDate) newErrors.quotationDate = 'Quotation Date is required';
        
        let hasItemErrors = false;
        if (!formData.items || formData.items.length === 0) {
            hasItemErrors = true;
            newErrors.items_global = true;
        } else {
            formData.items.forEach((item, index) => {
                if (!item.productName && !item.productId) {
                    hasItemErrors = true;
                    newErrors[`items_${index}_productName`] = 'Required';
                }
                if (item.quantity <= 0) {
                    hasItemErrors = true;
                    newErrors[`items_${index}_quantity`] = 'Required';
                }
            });
        }

        if (Object.keys(newErrors).length > 0 || hasItemErrors) {
            setErrors(newErrors);
            setSubmitError('Please check the highlighted fields above; the request cannot proceed.');
            return;
        }

        try {
            setIsLoading(true);
            const totalAmount = formData.items.reduce((sum, item) => sum + (parseFloat(item.quantity) * parseFloat(item.price)), 0);
            const requestData = {
                ...formData,
                totalAmount: totalAmount,
                items: formData.items.map(item => ({
                    productId: item.productId ? parseInt(item.productId) : null,
                    productName: item.productName,
                    quantity: parseInt(item.quantity),
                    price: parseFloat(item.price),
                    purchasePrice: parseFloat(item.purchasePrice),
                    lineTotal: (parseFloat(item.quantity) * parseFloat(item.price)),
                    customDescription: item.customDescription
                }))
            };
            await quotationAPI.createQuotation(requestData);
            resetDraft();
            if (onSuccess) onSuccess();
            setIsSuccess(true);
        } catch (err) {
            console.error('Error creating quotation:', err);
            const serverErrors = applyServerErrors(err);
            const msg = serverErrors._form || err.response?.data?.message || err.message || 'Failed to create quotation.';
            setSubmitError(msg);
            if (onError) onError(msg);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className={cn(
                "overflow-hidden flex flex-col p-0 transition-all duration-500 rounded-[3rem] border-0 shadow-2xl ring-1 ring-white/20 dark:bg-slate-950 dark:ring-white/10 dark:text-slate-100",
                isSuccess ? "max-w-md max-h-[500px]" : "max-w-[1000px] max-h-[95vh]"
            )}>
              {isSuccess ? (
                <SuccessScreen 
                    message="Quotation Generated!"
                    subMessage="Returning to proposals list..."
                    countdown={countdown}
                    onAction={() => setCountdown(0)}
                />
              ) : (
                <>
                <DialogHeader className="p-8 pb-4 border-b bg-white/40 dark:bg-slate-900/60 dark:border-slate-800 backdrop-blur-3xl sticky top-0 z-40">
                    <div className="flex items-center gap-5">
                        <div className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                            <div className="relative p-3.5 rounded-2xl bg-[#FF7D00]/10 text-[#FF7D00] shadow-sm transform hover:rotate-3 transition-transform duration-300">
                                <Plus className="w-8 h-8" />
                            </div>
                        </div>
                        <div>
                            <DialogTitle className="text-3xl font-black tracking-tight text-slate-800 dark:text-white flex items-center gap-2">
                                New Quotation
                                <span className="text-sm font-black px-3 py-1 bg-[#FF7D00]/10 text-[#FF7D00] rounded-full uppercase tracking-widest ml-1 shadow-sm">Proposal</span>
                            </DialogTitle>
                            <DialogDescription className="text-sm font-bold text-slate-500 dark:text-slate-400 mt-1 opacity-80">
                                Designing a professional value proposition for your partner.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="contents">
                    <div className="flex-1 overflow-y-auto px-8 py-8 scroll-smooth">
                    {submitError && (
                        <div className="mb-6 animate-in slide-in-from-top-4 duration-300">
                            <BeautifiedErrorAlert error={submitError} onDismiss={() => setSubmitError(null)} />
                        </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="customerName" className="text-xs font-black uppercase tracking-widest text-[#FF7D00]">Customer Name <span className="text-red-500">*</span></Label>
                            <Input
                                id="customerName"
                                value={formData.customerName}
                                onChange={(e) => {
                                    setFormData(prev => ({ ...prev, customerName: e.target.value }));
                                    if(errors.customerName) setErrors(prev => ({...prev, customerName: null}));
                                }}
                                placeholder="Enter customer name"
                                className={cn("jit-input", errors.customerName && "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake")}
                            />
                            {errors.customerName && <p className="text-[10px] font-black text-red-500 uppercase tracking-tighter ml-1">Required</p>}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="quotationDate">Quotation Date <span className="text-rose-500">*</span></Label>
                            <Input
                                id="quotationDate"
                                type="date"
                                value={formData.quotationDate}
                                onChange={(e) => {
                                    setFormData(prev => ({ ...prev, quotationDate: e.target.value }));
                                    if(errors.quotationDate) setErrors(prev => ({...prev, quotationDate: null}));
                                }}
                                className={cn("h-12 rounded-xl", errors.quotationDate && "border-rose-400 bg-rose-50/50 ring-2 ring-rose-500/10")}
                            />
                            {errors.quotationDate && <p className="text-xs font-bold text-rose-600 ml-1 mt-1">{errors.quotationDate}</p>}
                        </div>
                    </div>

                    <div className="space-y-2 mt-4">
                        <Label htmlFor="customerAddress">Customer Address</Label>
                        <Textarea
                            id="customerAddress"
                            value={formData.customerAddress}
                            onChange={(e) => setFormData(prev => ({ ...prev, customerAddress: e.target.value }))}
                            placeholder="Enter customer address details"
                            rows={2}
                            className="rounded-2xl border-slate-200"
                        />
                    </div>

                    <div className="space-y-4">
                        <QuotationItemForm
                            items={formData.items}
                            onAddItem={handleAddItem}
                            onUpdateItem={handleUpdateItem}
                            onRemoveItem={handleRemoveItem}
                            errors={errors}
                        />
                    </div>

                    <div className="space-y-4 bg-muted/20 p-6 rounded-3xl border border-dashed border-muted-foreground/20 mt-6">
                        <h4 className="text-sm font-black flex items-center gap-2 text-slate-600 uppercase tracking-widest">
                            <ClipboardList className="w-4 h-4" /> Terms & Special Notes
                        </h4>
                        <Textarea
                            placeholder="Add validity period, delivery terms, or any special notes for this quotation..."
                            value={formData.notes || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                            className="min-h-[100px] bg-white/50 border-slate-200 rounded-2xl p-4 text-sm font-medium"
                        />
                    </div>


                    </div>

                    <DialogFooter className="p-8 pt-4 border-t bg-slate-50/50 dark:bg-slate-900/60 dark:border-slate-800 sticky bottom-0 z-30 sm:justify-end items-center gap-4 shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.05)]">
                        <Button type="button" variant="destructive" onClick={resetDraft} className="mr-auto h-12 px-6 rounded-xl font-black uppercase text-[10px] tracking-widest opacity-80 hover:opacity-100" disabled={isLoading}>
                            Clear Draft
                        </Button>
                        <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading} className="h-12 px-8 rounded-xl font-bold text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100">
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isLoading} className="h-12 px-12 rounded-xl bg-[#FF7D00] hover:brightness-110 active:scale-95 text-white font-black uppercase tracking-widest shadow-xl shadow-orange-500/10 transition-all duration-200">
                            {isLoading ? (
                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            ) : 'Create Quotation'}
                        </Button>
                    </DialogFooter>
                </form>
                </>
              )}
            </DialogContent>
        </Dialog>
    );
};

export default CreateQuotationModal;
